import type { PendingMutation, UploadRecord } from "./types";

const DB_NAME = "gavial-titulky";
const VERSION = 1;

function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("uploads")) db.createObjectStore("uploads", { keyPath: "key" });
      if (!db.objectStoreNames.contains("mutations")) db.createObjectStore("mutations", { keyPath: "id", autoIncrement: true });
      if (!db.objectStoreNames.contains("state")) db.createObjectStore("state");
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function transaction<T>(storeName: string, mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await database();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const request = action(tx.objectStore(storeName));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
  });
}

export const db = {
  getState<T>(key: string): Promise<T | undefined> {
    return transaction("state", "readonly", (store) => store.get(key));
  },
  setState(key: string, value: unknown): Promise<IDBValidKey> {
    return transaction("state", "readwrite", (store) => store.put(value, key));
  },
  deleteState(key: string): Promise<undefined> {
    return transaction("state", "readwrite", (store) => store.delete(key));
  },
  putUpload(record: UploadRecord): Promise<IDBValidKey> {
    return transaction("uploads", "readwrite", (store) => store.put(record));
  },
  getUpload(key: string): Promise<UploadRecord | undefined> {
    return transaction("uploads", "readonly", (store) => store.get(key));
  },
  deleteUpload(key: string): Promise<undefined> {
    return transaction("uploads", "readwrite", (store) => store.delete(key));
  },
  addMutation(mutation: PendingMutation): Promise<IDBValidKey> {
    return transaction("mutations", "readwrite", (store) => store.add(mutation));
  },
  async putLatestMutation(mutation: PendingMutation): Promise<IDBValidKey> {
    const existing = (await this.allMutations()).filter(
      (item) => item.backend === mutation.backend && item.baseUrl === mutation.baseUrl && item.path === mutation.path,
    );
    for (const item of existing) if (item.id) await this.deleteMutation(item.id);
    return this.addMutation(mutation);
  },
  allMutations(): Promise<PendingMutation[]> {
    return transaction("mutations", "readonly", (store) => store.getAll());
  },
  deleteMutation(id: number): Promise<undefined> {
    return transaction("mutations", "readwrite", (store) => store.delete(id));
  },
};
